import { useSelector } from "react-redux";
import VLTable from "views/uikit/VLTable";
import { FoldersDropBox } from "views/uikit/FormFields";

import TooltipText from "./TooltipText";
import useCreateDataset from "hooks/useCreateDataset";
import useFolderSelection from "./useFolderSelection";
import useFolderValidation from "hooks/useFolderValidation";
import useFolderData, { FolderTableData } from "./useFolderData";
import { FolderMetadata } from "types";
import { getInvalidFolders } from "redux/CreateDataset/selectors";

import styles from "./style.module.scss";
import Loader from "../../Loader";
import { folderFormats } from "views/uikit/FormFields/FoldersDropBox";
import SupportedFormats from "views/uikit/FormFields/DragAndDropBase/SupportedFormats";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

interface AddDataContentProps {
  isLoading?: boolean;
  folders: FolderMetadata[];
  onFoldersChange: (folders: FolderMetadata[]) => void;
  onFolderUploadStart: (folderNames: string[]) => void;
  isTableLoading?: boolean;
}

export default function AddDataContent({
  folders,
  isLoading = false,
  onFoldersChange,
  onFolderUploadStart,
  isTableLoading,
}: AddDataContentProps) {
  // Get invalid folders from Redux state
  const invalidFolders = useSelector(getInvalidFolders);

  // Use validation hook to get validation state
  const { isValidating } = useFolderValidation();

  // Combine valid folders with invalid folders for display
  const allFolders = [...folders, ...invalidFolders];
  const hasFolders = allFolders.length > 0;

  const { startFolderUpload } = useCreateDataset();
  const {
    selectedRowKeys,
    disabledRowKeys,
    checkboxDisabledKeys,
    checkboxTooltipData,
    filterState,
    filteredFolders,
    handleSelectionChange,
    handleFilterChange,
    showSelectAll,
    handleSelectAll,
  } = useFolderSelection(allFolders, onFoldersChange);

  const { columns, dataSource } = useFolderData(
    filteredFolders,
    filterState,
    allFolders
  );

  const handleFilesChange = (newFiles: File[]) => {
    // Extract folder names from the uploaded files
    const folderNames = Array.from(
      new Set(
        newFiles.map((file) => {
          // Get the first directory name from the file path
          const pathParts = file.webkitRelativePath.split("/");
          return pathParts[0]; // First part is the folder name
        })
      )
    );

    // Notify parent about the upload start
    onFolderUploadStart(folderNames);

    startFolderUpload(newFiles);
  };
  // Show loading state during validation OR upload
  const showLoadingState = isLoading || isValidating;

  if (isTableLoading) {
    return <div className={styles.tableLoading}>{<Loader />}</div>;
  }

  return (
    <div className={styles.uploadFolderSection}>
      <div className={styles.dropboxWrapper}>
        <FoldersDropBox
          title="Drag and drop folder here"
          textButton="Browse Folders"
          tooltipText={<TooltipText />}
          displayFormats={!hasFolders}
          loading={showLoadingState}
          onFilesChange={handleFilesChange}
        />
      </div>

      {hasFolders && (
        <div className={styles.fileListSection}>
          <div className={styles.fileListHeaderWrapper}>
            <div className={styles.fileListHeader}>
              Select Folders
              <Tooltip
                placement={"right"}
                title={<SupportedFormats formatList={folderFormats} />}
              >
                <InfoCircleOutlined className={styles.infoIcon} />
              </Tooltip>
            </div>
          </div>
          <div className={styles.fileListTable}>
            <VLTable<FolderTableData>
              columns={columns}
              dataSource={dataSource}
              selectedRowKeys={selectedRowKeys}
              disabledRowKeys={disabledRowKeys}
              checkboxDisabledKeys={checkboxDisabledKeys}
              checkboxTooltipData={checkboxTooltipData}
              onTableFilterChange={handleFilterChange}
              onSelectionChange={handleSelectionChange}
              showSelectAll={showSelectAll}
              onSelectAll={handleSelectAll}
            />
          </div>
        </div>
      )}
    </div>
  );
}
